import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import { 
  getSoftwareAssets
} from '../../../models/assetsModel';
import ViewSoftwareModal from './modals/ViewSoftwareModal';
import EditSoftwareModal from './modals/EditSoftwareModal';
import AddSoftwareModal from './modals/AddSoftwareModal';

// Helper function to calculate days remaining
const calculateDaysRemaining = (expiryDate) => {
  if (!expiryDate) return 0;
  const today = new Date();
  const expiry = new Date(expiryDate);
  
  // Check if date is valid
  if (isNaN(expiry.getTime())) return 0;
  
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

// Helper function to strip HTML tags
const stripHtmlTags = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

function SoftwareAssets() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const initialPagination = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 20;
    return {
      pageIndex: page - 1,
      pageSize
    };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [globalFilter, setGlobalFilter] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('search') || '';
  });
  const [sorting, setSorting] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append('page', pagination.pageIndex + 1);
      params.append('pageSize', pagination.pageSize);
      
      if (globalFilter) {
        params.append('search', globalFilter);
      }
      
      if (sorting.length > 0) {
        params.append('sortBy', sorting[0].id);
        params.append('sortOrder', sorting[0].desc ? 'DESC' : 'ASC');
      }
       
      const response = await getSoftwareAssets(params);
      
      if (response.status === 1 && response.data) {
        // Handle nested data structure: response.data.data.rows
        const nestedData = response.data.data || response.data;
        const dataRows = nestedData.rows || nestedData;
        setAssets(Array.isArray(dataRows) ? dataRows : []);
        setRowCount(nestedData.total || (Array.isArray(dataRows) ? dataRows.length : 0));
      } else {
        toast.error(response.message || 'Failed to load software assets');
        setAssets([]);
        setRowCount(0);
      }
    } catch (error) {
      toast.error(error.message || 'An error occurred while loading software assets');
      setAssets([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [pagination, globalFilter, sorting]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  useEffect(() => {
    // Update the URL when pagination or filters change
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', pagination.pageIndex + 1);
    searchParams.set('pageSize', pagination.pageSize);
    
    if (globalFilter) {
      searchParams.set('search', globalFilter);
    } else {
      searchParams.delete('search');
    }
    
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    }, { replace: true });
  }, [pagination, globalFilter, navigate, location.pathname, location.search]);

  // Handle pagination change
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, pageIndex: newPage }));
  };

  // Handle entries per page change
  const handleEntriesPerPageChange = (e) => {
    const pageSize = Number(e.target.value);
    setPagination(prev => ({ ...prev, pageIndex: 0, pageSize }));
  };

  // Build pagination controls
  const totalPages = Math.ceil(rowCount / pagination.pageSize);
  const currentPage = pagination.pageIndex + 1;

  // Modal handlers
  const handleView = (asset) => {
    setSelectedAsset(asset);
    setShowViewModal(true);
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setShowEditModal(true);
  };

  const handleAssetUpdated = () => {
    setShowEditModal(false);
    fetchAssets();
  };

  const handleAssetAdded = () => {
    setShowAddModal(false);
    fetchAssets();
  };

  // Check permissions after all hooks are defined
  if (!can('View Assets')) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Access Denied</div>
          <div className="text-gray-600">You do not have permission to view software assets.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Section with Logo and Title */}
        <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  Software & Subscriptions
                </h1>
                <p className="text-xs text-gray-500 mb-1">
                  Manage software licenses and subscriptions
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {rowCount ? `Showing ${Math.min(pagination.pageSize, rowCount)} of ${rowCount} results` : '0 results'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
     
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search software assets..."
                    value={globalFilter ?? ""}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm placeholder-gray-400"
                  />
                </div>
              </div>
              {can('Manage Assets') && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1"
                style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0087D4' }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Software
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Assets Grid */}
        {loading ? (
          <div className="bg-white rounded-xl p-12 shadow-lg border border-gray-100 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading software assets...</p>
          </div>
        ) : assets.length === 0 ? (
          <div className="bg-white rounded-xl p-12 shadow-lg border border-gray-100 text-center">
            <div className="max-w-sm mx-auto">
              <div className="bg-gray-100 rounded-full p-6 w-20 h-20 mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No software assets found</h3>
              <p className="text-gray-500 mb-6">
                {globalFilter ? "No assets match your search criteria." : "Get started by adding your first software asset."}
              </p>
              {can('Manage Assets') && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1"
                  style={{ backgroundColor: '#0087D4', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0087D4' }}
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Software
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {assets.map((asset) => (
              <div key={asset.asset_id} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-200 hover:border-blue-200 group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 rounded-lg p-2.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                      <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {asset.asset_name}
                      </h3>
                      {asset.vendor && (
                        <p className="text-sm text-gray-500">{asset.vendor}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {can('Manage Assets') && (
                      <button
                        onClick={() => handleEdit(asset)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}
                    {can('View Assets') && (
                      <button
                        onClick={() => handleView(asset)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">Software:</span>
                    <span className="text-sm font-medium text-gray-900">{asset.asset_name || 'N/A'}</span>
                  </div>
                  
                  {asset.vendor && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Vendor:</span>
                      <span className="text-sm font-medium text-gray-900">{asset.vendor}</span>
                    </div>
                  )}

                  {asset.asset_date_purchased && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Purchase Date:</span>
                      <span className="text-sm font-medium text-gray-900">{asset.asset_date_purchased}</span>
                    </div>
                  )}

                  {asset.asset_expiry_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Expiry Date:</span>
                      <span className="text-sm font-medium text-gray-900">{asset.asset_expiry_date}</span>
                    </div>
                  )}

                  {asset.asset_users && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Licensed Users:</span>
                      <span className="text-sm font-medium text-gray-900">{asset.asset_users}</span>
                    </div>
                  )}

                  {asset.asset_expiry_date && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">Days Remaining:</span>
                      <span className={`text-sm font-medium ${
                        calculateDaysRemaining(asset.asset_expiry_date) <= 30 
                          ? 'text-red-600' 
                          : calculateDaysRemaining(asset.asset_expiry_date) <= 90 
                          ? 'text-amber-600' 
                          : 'text-green-600'
                      }`}>
                        {calculateDaysRemaining(asset.asset_expiry_date) || 0} days
                      </span>
                    </div>
                  )}

                  {asset.asset_description && (
                    <div className="pt-3 border-t border-gray-100">
                      <div className="flex items-start justify-between">
                        <span className="text-sm text-gray-500">Description:</span>
                        <span className="text-sm font-medium text-gray-900 text-right ml-2">
                          {stripHtmlTags(asset.asset_description)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom Pagination Controls */}
        {assets.length > 0 && (
          <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 mt-6">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Items per page:</span>
                <select
                  value={pagination.pageSize}
                  onChange={handleEntriesPerPageChange}
                  className="block w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={40}>40</option>
                <option value={60}>60</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">entries per page</span>
            </div>

            <span className="flex items-center gap-1 text-sm text-gray-600">
              Showing {Math.min(rowCount, pagination.pageIndex * pagination.pageSize + 1)} to {Math.min(rowCount, (pagination.pageIndex + 1) * pagination.pageSize)} of {rowCount} entries
            </span>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(0)}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(pagination.pageIndex - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <span className="flex items-center gap-1 text-sm text-gray-600 px-4">
                Page{' '}
                <strong>
                  {pagination.pageIndex + 1} of{' '}{totalPages}
                </strong>
              </span>
              
              <button
                onClick={() => handlePageChange(pagination.pageIndex + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedAsset && (
          <ViewSoftwareModal
            isOpen={showViewModal}
            onClose={() => setShowViewModal(false)}
            assetId={selectedAsset.asset_id}
          />
        )}

        {/* Edit Modal */}
        {showEditModal && selectedAsset && (
          <EditSoftwareModal
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSuccess={handleAssetUpdated}
            assetId={selectedAsset.asset_id}
          />
        )}

        {/* Add Modal */}
        {showAddModal && (
          <AddSoftwareModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            onSuccess={handleAssetAdded}
          />
        )}
      </div>
    </div> 
  );
}

export default SoftwareAssets;
